import { useEffect, useState } from 'react';
import { Button, Input, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState } from '../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../shared/icons';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { Modality } from '../../../shared/services/turnoGeo/types';

const repo = getTurnoGeoRepository();

/**
 * Configuración > Catálogo de modalidad: Presencial/Remoto/Fuera de rango
 * viven aquí como filas editables (decisión aprobada) - NO están quemadas en
 * un CHECK constraint fijo, para poder agregar o renombrar modalidades sin
 * necesitar una migración de base de datos.
 */
export function ModalityCatalogTab() {
  const toast = useToast();
  const [rows, setRows] = useState<Modality[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Modality | null>(null);
  const [form, setForm] = useState({ code: '', name: '', sortOrder: '0' });
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<Modality | null>(null);

  const load = () => {
    setIsLoading(true);
    repo.listModalities(true).then((r) => {
      setRows(r);
      setIsLoading(false);
    });
  };
  useEffect(load, []);

  const openCreate = () => {
    setForm({ code: '', name: '', sortOrder: String(rows.length + 1) });
    setCreating(true);
  };
  const openEdit = (r: Modality) => {
    setForm({ code: r.code, name: r.name, sortOrder: String(r.sortOrder) });
    setEditing(r);
  };
  const close = () => {
    setCreating(false);
    setEditing(null);
  };
  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.show('Código y nombre son obligatorios.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const payload = { code: form.code, name: form.name, sortOrder: Number(form.sortOrder) || 0 };
      if (editing) {
        await repo.updateModality(editing.id, payload);
        toast.show('Modalidad actualizada.');
      } else {
        await repo.createModality(payload);
        toast.show('Modalidad agregada.');
      }
      close();
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (r: Modality) => {
    await repo.updateModality(r.id, { isActive: !r.isActive });
    load();
  };

  const columns: Column<Modality>[] = [
    { key: 'code', filterValue: (r) => r.code, header: 'Código', render: (r) => r.code },
    { key: 'name', filterValue: (r) => r.name, header: 'Nombre', render: (r) => r.name, sortable: true, sortValue: (r) => r.name },
    { key: 'sortOrder', header: 'Orden', render: (r) => r.sortOrder },
    {
      key: 'activo',
      header: 'Estado',
      render: (r) => (
        <button onClick={() => toggleActive(r)}>
          <Badge tone={r.isActive ? 'success' : 'neutral'}>{r.isActive ? 'Activa' : 'Inactiva'}</Badge>
        </button>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Button leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>
        Agregar modalidad
      </Button>

      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="🗂️" title="Sin modalidades" description="Agrega al menos Presencial, Remoto y Fuera de rango." />
      ) : (
        <DataTable excel fixedKey="code" columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} emptyTitle="Sin modalidades" />
      )}

      <Modal open={creating || !!editing} onClose={close} title={editing ? 'Editar modalidad' : 'Agregar modalidad'}>
        <div className="space-y-3">
          <Input label="Código (interno, sin espacios)" placeholder="Ej. HIBRIDO" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          <Input label="Nombre visible" placeholder="Ej. Híbrido" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Orden" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={close}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Desactivar modalidad"
        description={`¿Desactivar "${deleting?.name}"? Dejará de estar disponible para horarios y clasificación de marcajes nuevos.`}
        tone="danger"
        confirmLabel="Desactivar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteModality(deleting.id);
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
